import { Box, Button, FormControl, InputAdornment, SvgIcon, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material';
import { Franchise } from '../../../app/services/api/generated';
import CustomPagination from '../../../components/forms/Pagination';
import { ConfigurationLevel } from '../enums/ConfigurationLevel';
import { PodConfigurationModel } from '../../../app/services/api/podClient';
import { ChangeEvent, useEffect, useState } from 'react';
import { mergeArrays } from '../helpers/ArrayHelper';
import { useTranslation } from 'react-i18next';
import { SearchSm } from '../../../assets';
import useNswagClient from './../../../hooks/api/useNswagClient';
import CustomSwitch from './../../../components/shared/CustomSwitch';

type IProps = {
  franchises: Franchise[],
  existingFranchiseConfigurations: PodConfigurationModel[],
  // eslint-disable-next-line no-unused-vars
  handleSave: (config: PodConfigurationModel) => void;
}

const FranchiseConfigurationsList = (props: IProps) => {
  const { t } = useTranslation('managePodConfigurations');
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const [search, setSearch] = useState<string>('');
  const [availableConfigs, setAvailableConfigs] = useState<PodConfigurationModel[]>([]);
  const [allConfigurations, setAllConfigurations] = useState<PodConfigurationModel[]>([]);
  const [selectedConfig, setSelectedConfig] = useState<PodConfigurationModel | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const { getFranchiseStores } = useNswagClient();

  useEffect(() => {
    prepareConfigurations();
    setPage(0);
  }, [props.franchises]);

  useEffect(() => {
    const configs = mergeArrays(availableConfigs, props.existingFranchiseConfigurations);
    const sortedConfigs = (configs ?? []).sort((a, b) => {
      return Number(b.enabled) - Number(a.enabled);
    });
    setAllConfigurations(sortedConfigs);
  }, [availableConfigs, props.existingFranchiseConfigurations]);

  const prepareConfigurations = () => {
    setAvailableConfigs(getAvailableConfigsFromFranchises());
  };

  const getAvailableConfigsFromFranchises = () => {
    return props.franchises.map((x) => {
      const config: PodConfigurationModel = {
        id: 0,
        franchiseName: x.franchiseName ?? '',
        storeNumber: '',
        storeName: '',
        starbucksStoreNumber: '',
        itemNumber: '',
        itemDescription: '',
        configurationLevel: ConfigurationLevel.Global,
        minStockOnHandQuantity: null,
        maxStockOnHandQuantity: null,
        safetyStock: null,
        isParEnabled: false,
        enabled: false,
        franchiseStores: [],
      };
      return config;
    });
  };

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>, index: number) => {
    const { name, checked } = event.target;

    let hasIndexChanged = false;
    const updatedItems = [...allConfigurations];
    const rows = visibleRows();

    const currentIndex = getConfigurationIndex(rows[selectedIndex]);
    const newIndex = getConfigurationIndex(rows[index]);

    if (selectedConfig && selectedIndex !== index) {
      updatedItems[currentIndex] = { ...selectedConfig };
      hasIndexChanged = true;
    }

    if (hasIndexChanged || !selectedConfig) {
      setSelectedConfig(allConfigurations[newIndex]);
    }

    updatedItems[newIndex] = { 
      ...updatedItems[newIndex], 
      [name]: checked,
    };

    setAllConfigurations(updatedItems);
    setSelectedIndex(index);
  };

  const handleCancel = (index: number) => {
    if (!selectedConfig) {
      return;
    }

    const rows = visibleRows();
    const actualIndex = getConfigurationIndex(rows[index]);

    const updatedItems = [...allConfigurations];
    updatedItems[actualIndex] = { ...selectedConfig };

    setAllConfigurations(updatedItems);
    setSelectedConfig(null);
    setSelectedIndex(-1);
  };

  const getConfigurationIndex = (config: PodConfigurationModel) => {
    return config ? allConfigurations.findIndex(x => x.franchiseName === config.franchiseName) : -1;
  };

  const handlePageChange = (pageNumber: number) => {
    handleCancel(selectedIndex);
    setPage(pageNumber);
  };

  const maxPages = () => {
    return Math.ceil(filteredRows().length / rowsPerPage);
  };

  const filteredRows = () => {
    const lowerCaseQuery = search.toLowerCase();
    return lowerCaseQuery === '' ? 
      allConfigurations : 
      allConfigurations.filter(x => x.franchiseName.toLowerCase().includes(lowerCaseQuery));
  };

  const handleSave = async (config: PodConfigurationModel) => {
    const result = await getFranchiseStores(config.franchiseName);
    const newConfig = structuredClone(config);
    if (result.data?.stores) {
      const franchiseStores = result.data.stores.map(x => ({
        storeNumber: x.storeNumber ?? '',
        starbucksStoreNumber: x.starbucksStoreNumber ?? '',
      }));

      newConfig.franchiseStores = franchiseStores;
    }

    props.handleSave(newConfig);
  };

  const visibleRows = () => {
    return filteredRows().slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchValue = event.target.value;

    handleCancel(selectedIndex);
    setSearch(newSearchValue);
    setPage(0);
  };

  const hasPendingChanges = (config: PodConfigurationModel) => JSON.stringify(selectedConfig) !== JSON.stringify(config);
  
  return (
    <>
      <FormControl sx={{ p: '10px' }}>
        <TextField placeholder={t('search')}
          size='small'
          autoComplete='off'
          id="search-input"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SvgIcon><SearchSm/></SvgIcon>
              </InputAdornment>
            ),
          }}
          value={search}
          onChange={handleSearchChange}
          fullWidth
        />
      </FormControl>
      <TableContainer sx={{ overflowY:'auto' }}>
        <Table sx={{ tableLayout: 'fixed', minWidth: 500, overflow: 'hidden' }}>
          <TableHead>
            <TableRow>
              <TableCell colSpan={3}>{t('franchiseName')}</TableCell>
              <TableCell sx={{ textAlign: 'center' }}>{t('predictiveOrdering')}</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {
              visibleRows().map((row, index) => (
                <TableRow
                  key={row.franchiseName}
                  sx={{ height: '55px' }}
                >
                  <TableCell colSpan={3}>
                    {row.franchiseName}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <CustomSwitch checked={row.enabled}
                      name='enabled'
                      onChange={($event) => handleCheckboxChange($event, index)} />
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    {
                      selectedIndex === index && hasPendingChanges(row) &&
                        <>
                          <Button
                            sx={{ m: 1 }}
                            variant="primary"
                            size="md"
                            onClick={() => handleSave(row)}>
                            {t('save')}
                          </Button>
                          <Button
                            sx={{ m:1 }}
                            variant="secondary"
                            size="md"
                            onClick={() => handleCancel(index)}>
                            {t('cancel')}
                          </Button>
                        </>
                    }
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <Box sx={{ minWidth: 500 }}>
          <CustomPagination
            page={page}
            setPage={handlePageChange}
            maxPages={maxPages()}
            breakpointForChangeDisplay={120000}
          ></CustomPagination>
        </Box>
      </TableContainer>
    </>
  );
};

export default FranchiseConfigurationsList;